/**
 * "Ideal" solar production, calculated in the browser.
 *
 * The /solar-production API returns each hour's sunlight (kWh per m2) and the
 * machine's current panel settings. The ideal is worked out here:
 *
 *   ideal kWh = sunlight x PV capacity (kW) x panel area (m2 per kW) x efficiency
 *
 * so changing efficiency or area on the machine (step-2 app) changes the graph on
 * the next load, without the server rewriting any stored hour. The same formula
 * is in enviz_api solar_model.ideal_kwh.
 */

const toNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Ideal kWh for one hour, or null when the sunlight or any panel setting is unusable. */
export function idealKwh(irradiationKwhM2, panel) {
  const sunlight = toNumber(irradiationKwhM2);
  const capacity = toNumber(panel?.pv_capacity);
  const efficiencyPct = toNumber(panel?.efficiency_pct);
  const area = toNumber(panel?.area_per_kw);
  if (sunlight === null || sunlight < 0) return null;
  if (!(capacity > 0) || !(efficiencyPct > 0) || !(area > 0)) return null;
  return sunlight * capacity * area * (efficiencyPct / 100);
}

/**
 * Rows for the Ideal-vs-Actual chart from a /solar-production payload.
 *
 * A missing value stays null so the chart draws a gap rather than a zero. Servers
 * that predate the sunlight fields only send the stored ideal; those values are
 * used as they are.
 */
export function buildSolarRows(payload) {
  const timestamps = Array.isArray(payload?.timestamps) ? payload.timestamps : [];
  const sunlight = payload?.solar_irradiation_kwh_m2;
  const stored = payload?.solar_energy_production;
  const actual = payload?.energy_negative_kwh;
  const live = Array.isArray(sunlight) && payload?.panel;

  return timestamps.map((timestamp, index) => ({
    timestamp,
    expected_generation_kwh: live
      ? idealKwh(sunlight[index], payload.panel)
      : toNumber(stored?.[index]),
    actual_generation_kwh: toNumber(actual?.[index]),
  }));
}
